/**
 * Workflow Registry Page (DRR-056)
 *
 * Lists available workflow configurations from the registry.
 * Hierarchy browser: Base -> State -> Industry -> Program
 * Stats: total workflows, inheritance depth, tenant overrides
 *
 * Pattern: follows RegulatoryRulesPage (stats cards + table + detail drawer)
 */

import { useMemo, useState, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  SimpleGrid,
  Loader,
  Button,
  TextInput,
  Select,
  Drawer,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSitemap,
  IconSearch,
  IconRefresh,
  IconHierarchy,
  IconAlertTriangle,
  IconShieldCheck,
  IconSettings,
  IconEye,
  IconGitBranch,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTenantId } from '@/auth';
import {
  useListWorkflowsQuery,
  type WorkflowFieldsFragment,
} from '@/generated/graphql';

/** Badge config for criticality levels */
const CRITICALITY_BADGE_CONFIG: Record<string, { color: string; label: string }> = {
  LIFE_CRITICAL: { color: 'red', label: 'Life-Critical' },
  CRITICAL: { color: 'orange', label: 'Critical' },
  HIGH: { color: 'yellow', label: 'High' },
  STANDARD: { color: 'blue', label: 'Standard' },
  LOW: { color: 'gray', label: 'Low' },
};

export function WorkflowRegistryPage() {
  const tenantId = useTenantId();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<WorkflowFieldsFragment | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const { data, loading, error, refetch } = useListWorkflowsQuery({
    variables: { limit: 200 },
    skip: !tenantId,
  });

  const configs = useMemo(() => data?.listWorkflows?.items ?? [], [data]);

  const filteredConfigs = useMemo(() => {
    let result = configs;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q)
      );
    }
    if (criticalityFilter) {
      result = result.filter((c) => c.criticality === criticalityFilter);
    }
    return result;
  }, [configs, searchQuery, criticalityFilter]);

  const stats = useMemo(() => ({
    total: configs.length,
    active: configs.filter((c) => c.active).length,
    lifeCritical: configs.filter((c) => c.criticality === 'LIFE_CRITICAL').length,
    critical: configs.filter((c) => c.criticality === 'CRITICAL').length,
    standard: configs.filter((c) => c.criticality === 'STANDARD' || c.criticality === 'LOW').length,
  }), [configs]);

  const handleRowClick = useCallback(
    (config: WorkflowFieldsFragment) => {
      setSelectedConfig(config);
      openDrawer();
    },
    [openDrawer]
  );

  const handleDrawerClose = useCallback(() => {
    closeDrawer();
    setSelectedConfig(null);
  }, [closeDrawer]);

  if (!tenantId) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view workflow registry configurations.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Registry">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Workflow Registry</Title>
            <Text size="sm" c="dimmed">
              Data-driven workflow configurations with 4-layer inheritance (DRR-056)
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="blue" size="lg">
                <IconSitemap size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">{stats.total}</Text>
                <Text size="xs" c="dimmed">Total Workflows</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="green" size="lg">
                <IconShieldCheck size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">{stats.active}</Text>
                <Text size="xs" c="dimmed">Active</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="red" size="lg">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">{stats.lifeCritical}</Text>
                <Text size="xs" c="dimmed">Life-Critical</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="orange" size="lg">
                <IconHierarchy size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">{stats.critical}</Text>
                <Text size="xs" c="dimmed">Critical</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="blue" size="lg">
                <IconSettings size={20} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">{stats.standard}</Text>
                <Text size="xs" c="dimmed">Standard</Text>
              </div>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Group gap="md">
          <TextInput
            placeholder="Search workflows..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by criticality"
            clearable
            data={[
              { value: 'LIFE_CRITICAL', label: 'Life-Critical' },
              { value: 'CRITICAL', label: 'Critical' },
              { value: 'HIGH', label: 'High' },
              { value: 'STANDARD', label: 'Standard' },
              { value: 'LOW', label: 'Low' },
            ]}
            value={criticalityFilter}
            onChange={setCriticalityFilter}
            w={200}
          />
        </Group>

        {/* Table */}
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Workflow</Table.Th>
              <Table.Th>Criticality</Table.Th>
              <Table.Th>Initial State</Table.Th>
              <Table.Th>Active</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredConfigs.slice(0, 50).map((config) => {
              const critBadge = CRITICALITY_BADGE_CONFIG[config.criticality ?? 'STANDARD'] ?? { color: 'blue', label: 'Standard' };
              return (
                <Table.Tr
                  key={config.id}
                  style={{
                    cursor: 'pointer',
                    borderLeft: config.criticality === 'LIFE_CRITICAL'
                      ? '3px solid var(--mantine-color-red-6)'
                      : undefined,
                  }}
                  onClick={() => handleRowClick(config)}
                >
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={500} size="sm">{config.name}</Text>
                      {config.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{config.description}</Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={critBadge.color} variant="light" size="sm">
                      {critBadge.label}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{config.initialState}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={config.active ? 'green' : 'gray'} variant="light" size="sm">
                      {config.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">v{config.version ?? '1'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconEye size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(config);
                      }}
                    >
                      View
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {filteredConfigs.length === 0 && (
          <Text ta="center" c="dimmed" py="xl">
            No workflow configurations found.
          </Text>
        )}
      </Stack>

      {/* Detail Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={handleDrawerClose}
        title="Workflow Configuration Details"
        position="right"
        size="lg"
      >
        {selectedConfig && (
          <Stack gap="md">
            <div>
              <Text size="xs" c="dimmed">Name</Text>
              <Text fw={500}>{selectedConfig.name}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Criticality</Text>
              <Badge
                color={
                  (CRITICALITY_BADGE_CONFIG[selectedConfig.criticality ?? 'STANDARD'] ?? { color: 'blue', label: 'Standard' })
                    .color
                }
                variant="light"
              >
                {
                  (CRITICALITY_BADGE_CONFIG[selectedConfig.criticality ?? 'STANDARD'] ?? { color: 'blue', label: 'Standard' })
                    .label
                }
              </Badge>
            </div>
            {selectedConfig.description && (
              <div>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{selectedConfig.description}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="dimmed">Initial State</Text>
              <Text size="sm">{selectedConfig.initialState}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Version</Text>
              <Text size="sm">v{selectedConfig.version ?? '1'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Active</Text>
              <Badge color={selectedConfig.active ? 'green' : 'gray'} variant="light">
                {selectedConfig.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* DRR-132: pipeline navigation links */}
            <Divider />
            <Group gap="xs">
              <Button
                size="sm"
                variant="light"
                leftSection={<IconGitBranch size={14} />}
                onClick={() => navigate(`/ferp-core/workflows/${selectedConfig.id}`)}
              >
                View diagram
              </Button>
              <Button
                size="sm"
                variant="subtle"
                color="violet"
                leftSection={<IconEye size={14} />}
                onClick={() =>
                  navigate(`/architect/workflow-observer?definitionId=${selectedConfig.id}`)
                }
              >
                Observe
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}

export default WorkflowRegistryPage;
